import { LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { Button, Card, Flex, Form, Space, Tag, Upload } from 'antd'
import { UploadFile } from 'antd/lib'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import {
  useStocksServiceGetInternalInquiryEndpoint,
  useStocksServiceGetIssuanceEndpoint
} from 'src/api/queries'
import {
  InternalInventoryDocumentState,
  InventoryDocumentState
} from 'src/api/requests'
import { ArrowSwapHorizontal } from 'src/assets'
import {
  CustomItemRender,
  LoadingScreen,
  PageBreadcrumbs,
  Text
} from 'src/components'
import { ConsumableListCreate } from 'src/features/stock/components'
import { getStatus } from 'src/features/stock/utils'
import { LocalApplicationCommentSection } from 'src/features/stock-local/components'
import { onDownloadFile } from 'src/hooks'
import { dayMonthYearHourMinutesFormatSeparator } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

dayjs.extend(utc)
dayjs.extend(timezone)

type Document = UploadFile & { id?: string }

export const StockLocalApplicationsDetails = () => {
  const [form] = Form.useForm()

  const { t } = useLocales()
  const navigate = useNavigate()

  const { id } = useParams()

  const printableRef = useRef<HTMLDivElement>(null)

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Application_${id}`
  })

  const onBackClick = () => {
    navigate(paths.stockLocal.applications)
  }

  const { data, isLoading } = useStocksServiceGetInternalInquiryEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    {
      enabled: !!id
    }
  )

  const isConfirmed =
    data?.state === InternalInventoryDocumentState.CONFIRMED ||
    data?.state === InternalInventoryDocumentState.ACCEPTED

  const isNew = data?.state === InternalInventoryDocumentState.NEW

  const { data: issuance, isLoading: isIssuanceLoading } =
    useStocksServiceGetIssuanceEndpoint(
      {
        id: String(data?.relatedDocuments?.[0]?.id)
      },
      [String(data?.relatedDocuments?.[0]?.id)],
      {
        enabled: !!data?.relatedDocuments?.[0]?.id && isConfirmed
      }
    )

  useEffect(() => {
    if (id && data) {
      form.setFieldsValue({
        comment: data?.comment,
        documentDate: data?.documentDate,
        inquiryItems: data?.items?.map((item) => ({
          name: `${item?.internationalName?.name} ${item?.internationalName?.dosage}`,
          internationalNameId: item?.internationalName?.id,
          form: {
            name: item?.productForm?.name,
            id: item?.productForm?.id
          },
          quantity: item?.quantity,
          items: item?.roleStocks || []
        }))
      })
    }
  }, [data, id, form])

  if (isLoading || isIssuanceLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={`${t('stock_applications.inquiry_number')}: #${data?.code}`}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.applications') },
        {
          title: `${t('stock_applications.inquiry_number')}: #${data?.code}`
        }
      ]}
    >
      <Flex vertical gap={24}>
        <Flex justify="space-between">
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>

          <Space size={8}>
            {data?.state === InternalInventoryDocumentState.CONFIRMED && (
              <Button
                onClick={() =>
                  navigate(paths.stockLocal.issuanceDetails(String(data?.id)))
                }
              >
                {t('stock_applications.see_application_details')}
              </Button>
            )}

            <Button icon={<PrinterOutlined />} onClick={() => onPrint()}>
              {t('stock_applications.print')}
            </Button>
          </Space>
        </Flex>

        <Form form={form}>
          <Flex gap={16} vertical ref={printableRef}>
            <Card>
              <Flex vertical gap={8}>
                <Flex justify="space-between">
                  <Space>
                    <Text weight={400}>
                      {t('stock_applications.inquiry_number')}:
                    </Text>
                    <Text strong>#{data?.code}</Text>
                  </Space>
                  <Tag
                    color={
                      getStatus(t, data?.state ?? InventoryDocumentState.NEW)
                        .color
                    }
                  >
                    {
                      getStatus(t, data?.state ?? InventoryDocumentState.NEW)
                        .text
                    }
                  </Tag>
                </Flex>
                <Space>
                  <Flex gap={8}>
                    <Text weight={400}>
                      {t('stock_applications.request_date')}:
                    </Text>
                    <Text strong>
                      {dayjs
                        .utc(data?.initializedOn)
                        .tz(dayjs.tz.guess())
                        .format(dayMonthYearHourMinutesFormatSeparator)}
                    </Text>
                    <ArrowSwapHorizontal />

                    <Text weight={400}>
                      {t('stock_applications.entry_date')}:
                    </Text>
                    <Text strong>
                      {issuance?.takeoverDate
                        ? dayjs
                            .utc(issuance?.takeoverDate)
                            .tz(dayjs.tz.guess())
                            .format(dayMonthYearHourMinutesFormatSeparator)
                        : '-'}
                    </Text>
                  </Flex>
                </Space>

                <Space>
                  <Text weight={400}>
                    {t('stock_applications.initiated_by')}:
                  </Text>
                  <Text strong>
                    {data?.initializedBy?.firstName}{' '}
                    {data?.initializedBy?.lastName}
                  </Text>
                  <Text strong>{data?.role && <>- {data?.role?.name}</>}</Text>
                </Space>
              </Flex>
            </Card>

            <Flex vertical gap={16}>
              <Card>
                <Form.Item name="inquiryItems">
                  <ConsumableListCreate isFinished={!isNew} form={form} />
                </Form.Item>
              </Card>
              <Card>
                <Flex vertical gap={16}>
                  <Card title={t('stock_applications.uploaded_documents')}>
                    <Upload
                      itemRender={(_, file: Document) => (
                        <CustomItemRender
                          file={file}
                          onDownload={() =>
                            onDownloadFile(t, file?.id, file?.name)
                          }
                        />
                      )}
                      defaultFileList={data?.mediaFiles?.map((file, index) => ({
                        ...file,
                        uid: String(index),
                        name: file?.name ?? `file-${index}`
                      }))}
                      disabled
                    />
                  </Card>

                  <LocalApplicationCommentSection data={data} />
                </Flex>
              </Card>
            </Flex>
          </Flex>
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
